import React from 'react';
import { Button } from '../components/ui/button.js';
import type { components } from '../api/schema.js';
import errorIcon from '../assets/error-icon.svg';
import infoIcon from '../assets/info-icon.svg';

/**
 * The authentication-hold-check result (CCF-43). Derived from the generated
 * OpenAPI client so the SPA stays in lock-step with the contract — a contract
 * change becomes a compile error here rather than drifting from a hand-written
 * duplicate.
 */
export type AuthHoldCheckResult = components['schemas']['AuthHoldCheckResult'];

/**
 * CCF-43 — Customer Authentication Hold Check pop-up.
 *
 * A single modal that surfaces, for the agent, the four item groups mandated by
 * the (16/Jun) story:
 *   (i)   Customer Information — last 4 of account, all names, account type;
 *   (ii)  Hold Type per account;
 *   (iii) Account opening date (omitted/NA for fraud holds);
 *   (iv)  Agent scripting per hold type.
 * For multiple accounts with multiple holds it carries ALL hold info in ONE
 * pop-up. AML holds (H/I/O) on a sole/all-account basis BLOCK the agent; fraud
 * holds (Q/8/V) allow log-in but no transactions on the affected account.
 */
export function AuthHoldPopup({
  result,
  onClose,
}: {
  result: AuthHoldCheckResult;
  onClose: () => void;
}): React.JSX.Element {
  const { blocked, fraudHold, accounts, customer } = result;
  // Is an AML (H/I/O) hold present? The per-account `holdClass` is 'aml' when
  // the account carries an H/I/O code (AML wins over fraud). This drives the
  // CCF-43 footer button (@dylan219 #784, 2026-09-11):
  //   - AML present but NOT a hard stop (a clean active account exists) → the
  //     "Acknowledge" progress button (agent acknowledges + proceeds). The #784
  //     relabel authorised dropping the #684 verbatim "Acknowledge – Cannot
  //     Proceed"; only the accessible name still distinguishes the "…and proceed"
  //     case.
  //   - AML hard stop (`blocked`) → NO progress button, only a neutral Close.
  //   - Fraud-only (Q/8/V, no AML) → NO Acknowledge button at all, only Close
  //     (@dylan219 #784, 2026-09-11: "The Q,v,8 hold should not have the
  //     acknowledge button at all"). The fraud hold allows log-in (servicing is
  //     not suppressed) — the pop-up is purely informational, so the agent reads
  //     the fraud scripting and closes it; there is no acknowledge/proceed action.
  const amlHoldPresent = accounts.some((a) => a.holdClass === 'aml');
  // The Acknowledge (progress) button is shown ONLY for the AML may-proceed case.
  // A fraud-only hold or an AML hard stop both render Close only.
  const showAcknowledge = amlHoldPresent && !blocked;
  const title = blocked
    ? 'Account access restricted'
    : fraudHold
      ? 'Fraud hold — log-in allowed, no transactions'
      : 'Account hold present';

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="auth-hold-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-6"
    >
      <div className="bg-white border-2 border-[#dad7d3] shadow-lg max-w-[720px] w-full max-h-[90vh] overflow-auto p-8">
        <div className="flex items-start gap-3 mb-6">
          <img
            src={blocked ? errorIcon : infoIcon}
            alt=""
            aria-hidden="true"
            className="w-6 h-6 mt-1 shrink-0"
          />
          <h2
            id="auth-hold-title"
            className={`text-[20px] font-['Arial',sans-serif] font-bold m-0 ${
              blocked ? 'text-[#dc2626]' : 'text-[#1d252c]'
            }`}
          >
            {title}
          </h2>
        </div>

        {/* (i) Customer Information */}
        <section className="mb-6">
          <h3 className="text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0 mb-2">
            Customer information
          </h3>
          <dl className="text-[14px] font-['Arial',sans-serif] text-[#1d252c] grid grid-cols-[160px_1fr] gap-x-4 gap-y-1">
            <dt className="font-bold">Account (last 4)</dt>
            <dd className="m-0">{customer.accountLast4 ?? '—'}</dd>
            <dt className="font-bold">Name(s)</dt>
            <dd className="m-0">
              {customer.names.length > 0 ? customer.names.join(', ') : '—'}
            </dd>
            <dt className="font-bold">Account type</dt>
            <dd className="m-0">
              {customer.accountTypes.length > 0
                ? customer.accountTypes.join(', ')
                : '—'}
            </dd>
          </dl>
        </section>

        {/* (ii)–(iv) per-account: hold type, opening date, scripting */}
        <section className="space-y-4">
          {accounts.map((a) => (
            <div
              key={a.index}
              className="border border-[#dad7d3] p-4 bg-[#F8EEE3]"
            >
              <dl className="text-[14px] font-['Arial',sans-serif] text-[#1d252c] grid grid-cols-[160px_1fr] gap-x-4 gap-y-1 mb-3">
                <dt className="font-bold">Account (last 4)</dt>
                <dd className="m-0">{a.accountLast4}</dd>
                <dt className="font-bold">Hold type</dt>
                <dd className="m-0">{a.holdCodes.join(', ')}</dd>
                <dt className="font-bold">Account opening date</dt>
                <dd className="m-0">{a.openingDate ?? 'N/A'}</dd>
              </dl>
              <p
                role="note"
                className="text-[14px] font-['Arial',sans-serif] text-[#1d252c] m-0 leading-[1.5] bg-white border-l-4 border-[#fc4c02] pl-3 py-2"
              >
                {a.scripting}
              </p>
            </div>
          ))}
        </section>

        <div className="mt-6 flex justify-end">
          {showAcknowledge ? (
            // AML may-proceed (a clean active account exists): the CCF-43 rule-B2
            // progress action — acknowledge + proceed (onClose). The visible
            // label is "Acknowledge" (@dylan219 #784 — relabel authorised;
            // supersedes the #684 verbatim "Acknowledge – Cannot Proceed"). The
            // accessible name still distinguishes the "…and proceed" case.
            <Button
              type="button"
              onClick={onClose}
              aria-label="Acknowledge authentication hold check and proceed"
              className="bg-[#FF703E] text-[#1D252C] border-2 border-[#FF703E] hover:bg-[#e55a2a] hover:border-[#e55a2a] rounded-md px-6 py-3 h-auto font-['Arial',sans-serif] font-bold text-[16px]"
            >
              Acknowledge
            </Button>
          ) : (
            // Either an AML hard stop (rule B2, #684 — every active account
            // carries an H/I/O hold, agent CANNOT proceed; `authHoldBlocked`
            // keeps servicing suppressed) OR a fraud-only Q/8/V hold (@dylan219
            // #784 — no acknowledge button at all; the fraud hold allows log-in,
            // so the pop-up is informational). Both render only a neutral Close,
            // which dismisses the window.
            <Button
              type="button"
              onClick={onClose}
              aria-label="Close authentication hold check"
              className="bg-[#FF703E] text-[#1D252C] border-2 border-[#FF703E] hover:bg-[#e55a2a] hover:border-[#e55a2a] rounded-md px-6 py-3 h-auto font-['Arial',sans-serif] font-bold text-[16px]"
            >
              Close
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
